/**
 * API がいま実際に受け付ける引数。**暫定の一覧で、いずれ消える**。
 *
 * スキーマ（schema/admin/schema.graphql）には並び順も絞り込みも一通り載っているが、
 * カバレッジの集計がまだデータ源から引けないため、API はそれに依存する引数を
 * `BAD_USER_INPUT` で断る。スキーマに書いてあるからといって画面から送ると、
 * 商品一覧を開いた瞬間にエラーになる。
 *
 * そこで「送ってよいもの」をここに 1 か所で宣言し、画面はここだけを見る。
 * 集計がつながったら、この表を広げるだけで画面が追従する。
 */

import type {
  CoverageBand,
  JobKind,
  PopulationId,
  RunStopPolicyValue,
  ProductOrder,
  WritebackMode,
  WritebackProposalState,
  WritebackTarget,
} from "visumo-data-agent-web-shared";

/** つながっていない軸を選ぼうとしたときに添える一言 */
export const NOT_CONNECTED_NOTE = "カバレッジの集計がつながると使えます";

/** いま API が honor する並び順 */
export const SUPPORTED_PRODUCT_ORDERS: readonly ProductOrder[] = ["NAME_ASC"];

/** 画面の既定。API が honor するものの中から選ぶ */
export const DEFAULT_PRODUCT_ORDER: ProductOrder = "NAME_ASC";

export const PRODUCT_ORDER_LABEL: Record<ProductOrder, string> = {
  NAME_ASC: "名前順",
  COVERAGE_ASC: "充足が低い順",
  COVERAGE_DESC: "充足が高い順",
  FAQ_DELIVERED_DESC: "配信している答えが多い順",
};

export const COVERAGE_BAND_LABEL: Record<CoverageBand, string> = {
  LOW: "低い",
  MIDDLE: "ふつう",
  HIGH: "高い",
};

/** いま API が honor する母集団 */
export const SUPPORTED_POPULATIONS: readonly PopulationId[] = [
  "DELIVERED",
  "ALL",
];

/**
 * いま API が honor する ProductFilter のキー。
 * ここに無いものは、値が入っていても送らない。
 */
export const SUPPORTED_PRODUCT_FILTER_KEYS: readonly string[] = [
  "query",
  "hasImage",
  "sellingCandidates",
  "answerable",
];

export function isSupportedProductOrder(order: ProductOrder): boolean {
  return SUPPORTED_PRODUCT_ORDERS.includes(order);
}

export function isSupportedPopulation(population: PopulationId): boolean {
  return SUPPORTED_POPULATIONS.includes(population);
}

export const POPULATION_LABEL: Record<PopulationId, string> = {
  DELIVERED: "配信対象の商品",
  ALL: "全商品",
  WITH_UGC: "UGC がある商品",
  TOP_SALES: "売上上位の商品",
  BY_CATEGORY: "カテゴリ別",
};

/* -------------------------------------------------------------------------
 * ジョブ（FAQ 生成）
 *
 * ジョブの口はスキーマ上 1 つの Job 型で通っているが、この縦切りで実際に
 * 動くのは FAQ 生成 1 本だけである。「型にあるから画面に出す」をやると、
 * 押しても何も起きない導線が増える。出してよい導線をここで宣言する。
 * ----------------------------------------------------------------------- */

/** 画面から起動できるジョブの種類。増えたらここを広げる */
export const SUPPORTED_JOB_KINDS: readonly JobKind[] = [
  "FAQ_GENERATION",
  "PERSONA_COVERAGE",
  "RULE_DRY_RUN",
];

/**
 * カバレッジ集計でいま API が honor する母集団。
 *
 * FAQ 生成が配信対象だけなのに対し、こちらは全商品も数えられる
 * （商品を読むだけで、LLM を呼ばないため）。売上上位・カテゴリ別は、
 * その集合を区別できる記録がデータ源に無いので、どちらでも選べない。
 */
export const SUPPORTED_COVERAGE_POPULATIONS: readonly PopulationId[] = [
  "DELIVERED",
  "ALL",
];

export function isSupportedCoveragePopulation(
  population: PopulationId,
): boolean {
  return SUPPORTED_COVERAGE_POPULATIONS.includes(population);
}

/**
 * FAQ 生成でいま API が honor する母集団。
 * 売上上位・カテゴリ別はその集合を区別できる記録がまだ無い。
 */
export const SUPPORTED_FAQ_GENERATION_POPULATIONS: readonly PopulationId[] = [
  "DELIVERED",
];

/** 画面の既定。honor するものの中から選ぶ */
export const DEFAULT_FAQ_GENERATION_POPULATION: PopulationId = "DELIVERED";

export function isSupportedFaqGenerationPopulation(
  population: PopulationId,
): boolean {
  return SUPPORTED_FAQ_GENERATION_POPULATIONS.includes(population);
}

/**
 * 失敗したぶんだけの再実行（`retryJob`）と、走っているジョブの取り消し
 * （`cancelJob`）は API にある。
 *
 * **画面に「できるかどうか」の判断を持たせない。** やり直せるかは
 * `Job.retryable` が言い（種別・状態・失敗件数・やり直し済みかを
 * サーバーが見ている）、取り消しがどこまで効いたかは `Job.cancelEffect` が
 * 言う。画面はどちらもそのまま出す（`pages/JobControls.tsx`）。
 * ここで真偽の定数を置くと、サーバーの判断と画面の判断が 2 つになる。
 */
export const JOB_CONTROLS_ARE_SERVER_DIRECTED = true;

/**
 * 進捗を引き直す間隔は **サーバーが指示する**（`Job.suggestedPollIntervalSec`）。
 * 画面が秒数を決め打ちしない。null は「終端なので止めてよい」の意味である。
 */
export const POLL_INTERVAL_IS_SERVER_DIRECTED = true;

/**
 * 固定応答の生成器。`Faq.generator` がこの値なら、中身を見ずに作られたもの。
 * 本物の文言と見分けがつかないまま配信の判断をしないよう、画面で必ず断る。
 */
export const STUB_FAQ_GENERATOR = "stub";

/**
 * モデルにつながった生成器の接頭辞。`Faq.generator` は `bedrock:<モデル ID>` の形で、
 * **どのモデルで作ったかが値に残る**。画面は接頭辞だけを見る(モデル ID を
 * 差し替えても画面を直さなくてよい)。
 */
export const MODEL_FAQ_GENERATOR_PREFIX = "bedrock:";

export const STUB_FAQ_NOTE = "この環境では固定の応答で生成しています。";

/**
 * AI が作ったものだと断る一言。
 *
 * 固定応答のときと**別の文言**にする。「固定の応答」と出したまま本物を流すと、
 * 読んだ人は下書きの確認が要らないものと受け取る。逆に本物に「固定の応答」と
 * 出すのも同じだけ誤解を生む。
 */
export const MODEL_FAQ_NOTE =
  "AI が生成した下書きです。配信前に内容を確認してください。";

/* -------------------------------------------------------------------------
 * ペルソナ別のカバレッジ
 * ----------------------------------------------------------------------- */

/**
 * 規則ベースの判定器の名前（`PersonaCoverage.judge`）。
 *
 * 画面はこの接頭辞を見て「推論ではなく決まった規則で数えた」と断る。
 * 判定器を差し替えたら値が変わるので、**値そのものを画面に埋め込まない**。
 */
export const RULE_BASED_JUDGE_PREFIX = "rule-based";

export const RULE_BASED_JUDGE_NOTE =
  "決まった規則（商品の説明・属性・FAQ の文字の照合）で数えています。AI の推論は使っていません。";

/**
 * 判定に使った仕組みが分からないときの一言。`judge` が null なのは
 * **まだ一度も集計していない**ときで、数え方の話ではない。
 */
export const UNKNOWN_JUDGE_NOTE = "何で数えたかが記録に残っていません。";

export function isRuleBasedJudge(judge: string | null | undefined): boolean {
  return typeof judge === "string" && judge.startsWith(RULE_BASED_JUDGE_PREFIX);
}

/**
 * 集計の起動から、画面が「もう一度押せる」と判断するまでの終端の状態。
 * ここに無い状態はまだ動いているものとして扱う。
 */
export const TERMINAL_JOB_STATES: readonly string[] = [
  "SUCCEEDED",
  "SUCCEEDED_WITH_ERRORS",
  "FAILED",
  "CANCELLED",
];

/* -------------------------------------------------------------------------
 * ルール
 * ----------------------------------------------------------------------- */

/**
 * ルール単位の件数（生成した答え・配信している答え）は **API に無い**。
 *
 * SDL から意図して落としてある（ルール単位で数える仕組みがまだ無い。FR14）。
 * 0 と書くと「1 件も作られていない」と読めるので、画面はこの一言を出す。
 */
export const RULE_COUNTS_NOT_MEASURED = "数えていません";

export const RULE_COUNTS_NOT_MEASURED_NOTE =
  "ルール単位で生成した答え・配信している答えを数える仕組みがまだありません。0 件という意味ではありません。";

/** ルールを消す口は API に無い。作ったものは残る */
export const SUPPORTS_RULE_DELETE = false;

/**
 * 試す（dry run）で一度に扱える商品の件数の上限。
 * 費用が出るので、名指ししないときはここまでしか試さない。
 */
export const DRY_RUN_MAX_SAMPLE_SIZE = 20;

/** 試す段の既定の件数。上限より小さくしてあるのは、押した回の費用を抑えるため */
export const DRY_RUN_DEFAULT_SAMPLE_SIZE = 3;

/**
 * 台帳をルールで絞る軸が無い（`JobFilter` に `ruleId` が無い）。
 *
 * 画面を開き直したときに拾い直せるのは **テナント全体で動いているジョブ**までで、
 * 「このルールのぶん」には絞れない。画面はそのとおりに断る。
 */
export const SUPPORTS_JOB_FILTER_BY_RULE = false;

export const TENANT_WIDE_JOB_NOTE =
  "この会社でいま動いている生成です。どのルールのぶんかは台帳から絞れません。";

/**
 * 配信する / 止めるを押せるかどうかは、役割の表に加えて
 * テナント設定（`runStopPolicy`）が決める。**画面は判定を持たない** —
 * 押した結果 `FORBIDDEN` で断られたら、その断りをそのまま出す。
 * 設定そのものを変えられるかも、サーバーが `editableByViewer` で返す。
 */
export const RUN_STOP_POLICY_IS_SERVER_SIDE = true;

/** 設定の値の表示名。API の enum をそのまま画面へ出さない。 */
export const RUN_STOP_POLICY_LABEL: Record<RunStopPolicyValue, string> = {
  ADMIN_AND_CS_ONLY: "管理者と visumo のサポートだけ",
  EDITOR_ALLOWED: "編集者にも許す",
};

/** 設定が何に効くかの説明。**押したら配信が始まる設定ではない**。 */
export const RUN_STOP_POLICY_NOTE =
  "ルールの配信を始める・止める、配信する範囲を切り替える、といった操作を誰が押せるかの設定です。ここを変えても配信そのものは動きません。";

export const RUN_STOP_FORBIDDEN_NOTE =
  "この会社の設定では、配信の切り替えができる人が限られています。";

/* -------------------------------------------------------------------------
 * 商品知識
 * ----------------------------------------------------------------------- */

/**
 * 商品知識の充足でいま API が honor する母数。
 *
 * 既定は **`ALL`**。機械生成の属性が載るデータ源に「配信中か」の列が無いので、
 * `DELIVERED` を選ぶと機械生成の属性群は数えずに `dataGap` が返る
 * （分子と分母の範囲が食い違う割合を出さないため）。選べはするので一覧には
 * 残し、選んだときに何が起きるかを画面で先に断る。
 */
export const SUPPORTED_KNOWLEDGE_POPULATIONS: readonly PopulationId[] = [
  "DELIVERED",
  "ALL",
];

/** 画面の既定。範囲が揃う母数を初期値にする */
export const DEFAULT_KNOWLEDGE_POPULATION: PopulationId = "ALL";

export function isSupportedKnowledgePopulation(
  population: PopulationId,
): boolean {
  return SUPPORTED_KNOWLEDGE_POPULATIONS.includes(population);
}

/** 配信対象を母数にしたときに、先に断る一言 */
export const KNOWLEDGE_DELIVERED_POPULATION_NOTE =
  "配信対象だけを母数にすると、機械生成の属性群は「—」になります。機械生成の側に「配信中か」の記録が無く、数え分けられないためです。";

/**
 * 素材の件数（`KnowledgeCoverage.sources`）のうち、機械生成の行がある商品の鍵。
 *
 * **充足の割合だけを出さない**ための数字である。機械生成がまだ 1 件も
 * 走っていない環境では、属性の充足は正しく 0 % になるが、それだけを大きく
 * 出すと「属性を入れそこねている」と読まれる。
 */
export const KNOWLEDGE_GENERATED_SOURCE_KEY = "semantics";

export const KNOWLEDGE_NOT_GENERATED_NOTE =
  "商品画像からの機械生成がまだ 1 件も走っていません。下の充足が 0 % なのは、属性を入れそこねているからではなく、生成の結果そのものがまだ無いためです。";

/**
 * 属性の機械生成は、**この画面から起動しない**。起動の口はオペレーター向けの側に
 * 作ると決めてある。ここに起動のボタンを置くと、同じ属性を埋める経路が 2 本になり、
 * どちらの版が入ったのかが出所から追えなくなる。
 */
export const KNOWLEDGE_GENERATION_IS_READ_ONLY_NOTE =
  "ここに出るのは、最後に走った機械生成の結果です。この画面から生成を始めることはできません（始める口は運用側にあります）。";

/**
 * 属性ごとの確からしさ（`KnowledgeAttribute.confidence`）は
 * **データ源に列が無いので常に null**。進捗バーを 0 % で描かない。
 */
export const KNOWLEDGE_CONFIDENCE_NOT_MEASURED = true;

/**
 * カテゴリ別の必須度（`AttributeSchemaRow.levels`）は登録先が無いので常に空。
 * 「どのカテゴリでも不要」ではなく「登録がまだ無い」である。
 */
export const ATTRIBUTE_LEVELS_NOT_REGISTERED_NOTE =
  "カテゴリごとの必須度は、まだどこにも登録されていません。不要という意味ではありません。";

/**
 * 語ごとの出現件数（`VocabularyTerm.count`）を数える集計はまだ無い。
 * 常に null で返るので、0 件と描かない。
 */
export const VOCABULARY_TERM_COUNT_NOT_MEASURED_NOTE =
  "語ごとに何件の商品が付いているかは、まだ数えていません。";

/** 統制語彙は参照だけで、管理画面から編集する口は無い */
export const SUPPORTS_VOCABULARY_EDIT = false;

/** 属性の並べ替え・属性群の追加の口もまだ無い */
export const SUPPORTS_ATTRIBUTE_REORDER = false;

export function isStubGenerated(generator: string | null | undefined): boolean {
  return generator === STUB_FAQ_GENERATOR;
}

export function isModelGenerated(
  generator: string | null | undefined,
): boolean {
  return (
    typeof generator === "string" &&
    generator.startsWith(MODEL_FAQ_GENERATOR_PREFIX)
  );
}

/* -------------------------------------------------------------------------
 * AIO 対策ハブ / 商品ページ
 * ---------------------------------------------------------------------- */

/**
 * 数えていない欄に出す文言。**「—」だけを置かない**。
 *
 * 「—」は「0 件」とも「数えていない」とも読める。読み手が前者だと思い込むと、
 * 取り込みを入れそこねていると誤解する（FR14 が避けたい唯一のこと）。
 */
export const NOT_MEASURED = "数えていません";

/**
 * 見出しの「回答可能率」と、工程ごとの数字は**別々の数え方**である。
 *
 * 見出しは集計が書いたスナップショットの 1 つの数字で、工程の数字は読み取り時に
 * それぞれの出どころから数えたもの。並べると「率は 99 % なのに工程 1 は数えて
 * いない」が食い違って見えるので、**定義が別物であることを画面に書く**。
 * 数字そのものは変えない。
 */
export const ANSWER_RATE_IS_A_SEPARATE_MEASURE_NOTE =
  "この率は集計が書いたスナップショットの数字で、下の工程ごとの数字とは数え方も数えた時刻も別です。工程の数字をこの率に合成していないので、工程が「数えていません」でも、この率が下がったり 0 になったりする関係にはありません。";

/**
 * `publishBreakdown` は API に載っていない。
 *
 * 設計の型は generated / delivered / stoppedByRule / droppedByQuota /
 * quotaTotal がすべて non-null の Int で、いまテナント全体で数えられるものが
 * 1 つも無い。0 で埋めた表を出すと「配信して 0 件だった」と読める。
 */
export const SUPPORTS_PUBLISH_BREAKDOWN = false;

/** 配信（公開の切り替え・配信ログ）はこの画面群の範囲外 */
export const PUBLISH_IS_NOT_IN_THIS_RELEASE =
  "配信（公開の切り替えと配信ログ）は、この画面ではまだ扱いません。公開した数の記録も持っていないので、内訳は出せません。";

/** 商品ページは**プレビューだけ**。ここから公開される口は無い */
export const PAGE_IS_PREVIEW_ONLY_NOTE =
  "組み立てた本文と JSON-LD は、まだどこにも公開されていません。中身を確かめるためのプレビューです。";

/** 一度に組み立てられる商品の数（サーバー側の上限と揃える） */
export const MAX_PAGE_GENERATION_TARGETS = 20;

/**
 * 商品ページの組み立ては **LLM を呼ばない**ので、見積もりの口が無い。
 * 画面は費用の確認を挟まず、押したらそのまま起票する。
 */
export const PAGE_GENERATION_HAS_NO_ESTIMATE_QUERY = true;

/* -------------------------------------------------------------------------
 * 売り方の提案 / レギュレーションブック
 * ---------------------------------------------------------------------- */

/**
 * 案の文は **LLM を呼ばずに組み立てている**。属性の値から決まる決まった文で、
 * 押すたびに変わることはない。
 */
export const SELLING_IDEA_IS_NOT_GENERATED_NOTE =
  "案の文は、入っている属性の値から組み立てています。AI に書かせてはいないので、同じ属性なら同じ文になります。";

/**
 * 狙う層（`segment`）は API に載っていない。層別に案を変える材料が
 * どのデータ源にも無いので、選ばせる欄そのものを置かない。
 */
export const SUPPORTS_SELLING_SEGMENT = false;

/**
 * 露出・流入・CV の枠も API に載っていない。運んでくる外部接続がまだ無く、
 * 空の枠を置くと「つないだのに 0 だった」と読める。
 */
export const SELLING_METRICS_NOT_CONNECTED_NOTE =
  "露出や流入との突き合わせは、指標を運んでくる外部の接続ができてからになります。いまはこの画面に枠を置いていません。";

/** 採用しても、外部へ書き戻したり配信したりはしない */
export const SELLING_ADOPTION_IS_RECORD_ONLY_NOTE =
  "採否はここに記録するだけです。採用しても、商品ページや配信先へ自動では反映されません。";

/**
 * 運用で直された差分からの学習候補は API に載っていない。
 * 差分をどこから拾うか（公開後の本文か、編集画面の履歴か）が決まっていない。
 */
export const SUPPORTS_REGULATION_DIFF_CANDIDATES = false;

/**
 * この規約を読んで生成に効かせている経路はまだ無い。
 * 「どこに効くか」を一覧で出すと、効いていない先まで効いて見える。
 */
export const REGULATION_NOT_APPLIED_YET_NOTE =
  "ここに書いた決めごとは、まだ文章の生成には効いていません。効かせる先を決めるところからで、いまは書きためる場所です。";

/** 1 行の長さと、1 つの区画に入る行数の上限（サーバー側と揃える） */
export const MAX_REGULATION_TEXT_LENGTH = 200;
export const MAX_REGULATION_ROWS = 200;

// ---------------------------------------------------------------------------
// AI Question Map（想定質問のひな形とカバレッジ）
// ---------------------------------------------------------------------------

/**
 * 共通ひな形（visumo が育てるもの）の正本がまだ無い。
 *
 * デモには一覧が並んでいるが、あれはダミーである。ダミーを本物の画面へ
 * 持ち込むと、テナントが「visumo が用意してくれた問い」として読む。
 */
export const SUPPORTS_SHARED_QUESTION_TEMPLATES = false;

export const SHARED_QUESTION_TEMPLATES_NOTE =
  "visumo 側の共通ひな形は、まだ正本がありません。ここに並ぶのは、この会社で足した問いだけです。";

/**
 * 根拠の内訳（設計の `evidenceBreakdown`）は API に載っていない。
 * 行の件数がすべて non-null の Int で、レビュー・UGC・動画のどれも
 * まだ取り込んでいない。0 で埋めると「数えたら 0 件だった」と読める。
 */
export const SUPPORTS_QUESTION_EVIDENCE_BREAKDOWN = false;

export const QUESTION_EVIDENCE_BREAKDOWN_NOTE =
  "答えの根拠が何から来ているかの内訳は数えていません。レビュー・投稿・動画をまだ取り込んでいないので、内訳を出すと商品マスターだけが 100 % に見えます。";

/**
 * 「この問いが何件の答えになったか」は数えていない。
 * 作った答えに、どの問いから出たかの紐付けが無い。
 */
export const QUESTION_ANSWER_COUNT_NOT_LINKED = true;

/**
 * 数え直しは AI Question Map 専用のジョブではなく、ペルソナ別の充足と
 * 同じ実行が行う。**押す場所を 2 つ作らない**ための注記。
 */
export const QUESTION_COVERAGE_SHARES_PERSONA_JOB_NOTE =
  "この画面の数字は、ペルソナ別の充足と同じ集計が書いています。数え直すには「ペルソナ別の充足」から数え直しを実行してください。";

/** 問いと対象カテゴリの長さの上限（サーバー側と揃える） */
export const MAX_QUESTION_TEXT_LENGTH = 200;
export const MAX_QUESTION_SCOPE_LENGTH = 60;

/* -------------------------------------------------------------------------
 * 配信 / 外部への書き戻し
 *
 * この画面群には **外部へ実際に書き込む・配信する口が 1 つも無い**。
 * 「まだ作っていない」と「効かない」を画面で言い分けるための文言をここに集める。
 * ----------------------------------------------------------------------- */

/** 公開の切り替えでいま API が honor する母数（公開の範囲の母数セレクタ）。 */
export const SUPPORTED_PUBLISH_POPULATIONS: readonly PopulationId[] = [
  "DELIVERED",
  "ALL",
];

export const DEFAULT_PUBLISH_POPULATION: PopulationId = "DELIVERED";

/**
 * 公開の切り替えは**設定の保存だけ**で、配信は走らない。
 * 押したら公開される、と読ませないための一言。
 */
export const PUBLISH_TOGGLE_IS_SETTING_ONLY_NOTE =
  "ここでの切り替えは設定を保存するだけです。押しても配信は始まりません。配信の仕組みは 11 月以降に作ります。";

/**
 * 配信を走らせる口（設計の `startPublishRefresh`）は API に無い。
 * 走らせる先が 1 つも無いので、押せる形で置かない。
 */
export const SUPPORTS_PUBLISH_REFRESH = false;

/** 配信ログが空であることの説明。**0 件の失敗ではない**。 */
export const DELIVERY_LOG_EMPTY_NOTE =
  "配信を始めていないため、送った記録がまだありません。失敗が 0 件なのではなく、実行そのものがまだありません。";

/**
 * 公開の範囲のうち、数えていない欄の説明。
 * 組み立て済みのページ数をテナント全体で数える口が無い。
 */
export const PUBLISH_SCOPE_NOT_COUNTED_NOTE =
  "組み立て済みのページ数と、そこから落ちている数は数えていません。0 件という意味ではありません。";

/** 書き戻し先の表示名。API の enum をそのまま画面へ出さない。 */
export const WRITEBACK_TARGET_LABEL: Record<WritebackTarget, string> = {
  PRODUCT_COPY: "EC の商品詳細の原稿",
  PRODUCT_ATTRIBUTE: "EC の商品属性",
  CONTENT_TAG: "投稿と商品のタグ付与",
  TAG_FEED_ORDER: "タグ配信の並び順",
};

export const WRITEBACK_MODE_LABEL: Record<WritebackMode, string> = {
  NEVER: "触らない",
  PROPOSE_ONLY: "提案まで（承認してから反映）",
  AUTOMATIC: "そのまま反映",
};

export const WRITEBACK_PROPOSAL_STATE_LABEL: Record<
  WritebackProposalState,
  string
> = {
  PENDING: "承認待ち",
  APPROVED: "承認済み（反映待ち）",
  APPLIED: "反映済み",
  REJECTED: "却下",
  REVERTED: "取り消し済み",
  FAILED: "失敗",
};

/**
 * `AUTOMATIC` を選んでも反映は走らない。**承認を省いたことにしない**。
 * 省いたことにすると、反映されていないものが「自動で反映済み」に見える。
 */
export const WRITEBACK_AUTOMATIC_NOT_CONNECTED_NOTE =
  "「そのまま反映」を選んでも、EC 側の口がつながるまでは反映されません。提案は承認待ちとして積まれます。";

/** 承認しても反映は走らない。押した人にそのまま出す文言。 */
export const WRITEBACK_APPROVE_DOES_NOT_APPLY_NOTE =
  "承認しました。反映は、EC 側の口がつながってから行います。";

/**
 * 反映前の値を持っていないので、取り消しの口も置かない。
 * 設計の `revertWriteback` は API に無い。
 */
export const SUPPORTS_WRITEBACK_REVERT = false;

export const WRITEBACK_NO_PREVIOUS_VALUE_NOTE =
  "反映前の値を持っていないので、取り消しはできません。EC 側のいまの原稿を読む口がまだありません。";

/** 提案が 1 件も無いときの文言。**「承認待ちが無い」と読ませない**。 */
export const WRITEBACK_PROPOSALS_EMPTY_NOTE =
  "まだ提案がありません。商品ページのプレビューから提案できます。";

/**
 * 状態で絞ったときの空表示の文言。
 *
 * **「1 つも無い」とは書かない。** 絞った結果が 0 件でも、ほかの状態の提案は
 * 残っている。終わった提案を片づける口が API に無いので、履歴は必ず増える。
 * 作り方も併せて書く（既定の「承認待ち」で 0 件のときに、次の一手が分かる）。
 */
export const WRITEBACK_PROPOSALS_FILTERED_EMPTY_NOTE =
  "ほかの状態の提案は、状態の切り替えで見られます。新しい提案は、商品ページのプレビューから作れます。";

/** 商品ページのプレビューから提案するときの一言。 */
export const PROPOSE_FROM_PAGE_NOTE =
  "組み立てた本文を、EC の商品詳細の原稿として反映する提案を作ります。提案を作るだけで、反映は走りません。";

// ---------------------------------------------------------------------------
// AI との連携（担当者ごとの MCP 接続キー）
// ---------------------------------------------------------------------------

/**
 * 共有範囲と許可操作は**どれも動かせない**（API に設定の口が無い）。
 *
 * 設計には `setAiShareScope` / `setAiOperation` があるが、読み取りに限る・
 * 投稿者の個人情報は常に無効、という不変条件（INV-6 / INV-7）のせいで
 * 動かせるつまみが 1 つも無い。押せるスイッチを描くと「設定できるのにできない」
 * ことになるので、鍵の印と理由だけを出す。
 */
export const SUPPORTS_AI_SHARE_SCOPE_EDIT = false;

export const AI_CONNECTION_LOCKED_NOTE =
  "共有範囲と許可操作は変えられません。AI に渡すのは読み取りだけで、投稿者の個人情報はどの設定でも渡しません。書き換えはどの担当者にも許可できません。";

/**
 * 利用量（最終利用・今月の呼び出し数）は**数えていない**。
 *
 * 0 と描かない。1 度も使っていないのではなく、記録そのものがまだ無い。
 */
export const SUPPORTS_AI_CONNECTION_USAGE = false;

export const AI_CONNECTION_USAGE_NOT_MEASURED_NOTE =
  "この画面では、誰がいつ何回使ったかを数えていません。0 回だったのではなく、呼び出しごとの記録をまだ残していません。";

/** 使っているモデル名を受け取る口がまだ無い（クライアントの自己申告）。 */
export const SUPPORTS_AI_CONNECTION_MODEL = false;

/** 接続キーは発行直後の 1 回しか表示できない（再表示の口を作らない）。 */
export const AI_CONNECTION_PLAINTEXT_SHOWN_ONCE_NOTE =
  "この値は二度と表示できません。いまコピーして、担当者へ安全な方法で渡してください。控えそびれたときは、この接続を止めてから発行し直します。";

/** 失効は次の呼び出しから効く（すでに始まっている処理は止まらない）。 */
export const AI_CONNECTION_REVOKE_NOTE =
  "止めると、次の呼び出しから使えなくなります。";

/** テナントあたりの有効な接続の上限（サーバー側と揃える） */
export const MAX_ACTIVE_AI_CONNECTIONS = 20;

/** 担当者名・社員 ID・部署の長さの上限（サーバー側と揃える） */
export const MAX_AI_MEMBER_NAME_LENGTH = 60;
export const MAX_AI_MEMBER_ID_LENGTH = 60;
export const MAX_AI_DEPARTMENT_LENGTH = 60;

/* -------------------------------------------------------------------------
 * 開発者向けの API キーと、呼び出しを許可するオリジン
 * ---------------------------------------------------------------------- */

/** キーの平文は発行直後の 1 回しか表示できない（再表示の口を作らない）。 */
export const API_KEY_PLAINTEXT_SHOWN_ONCE_NOTE =
  "この値は二度と表示できません。いまコピーして、サイトの設定へ安全な方法で入れてください。控えそびれたときは、このキーを失効させてから発行し直します。";

/** 失効は次の呼び出しから効く。 */
export const API_KEY_REVOKE_NOTE =
  "失効させると、次の呼び出しから使えなくなります。サイトに埋めたキーを先に差し替えてください。";

/** 管理者以外に出す 1 行。判断の正本はサーバーの `editableByViewer`。 */
export const API_ACCESS_FORBIDDEN_NOTE =
  "API キーと許可するオリジンを操作できるのは、この会社の管理者だけです。";

/** キーの用途の長さの上限（サーバー側と揃える）。 */
export const MAX_API_KEY_LABEL_LENGTH = 60;

/** 許可できるオリジンの件数の上限（サーバー側と揃える）。 */
export const MAX_ALLOWED_ORIGINS = 20;

/** オリジンの書き方の説明。サーバー側の検査と同じことを、押す前に読める形で。 */
export const ALLOWED_ORIGIN_FORMAT_NOTE =
  "1 行に 1 つ、https:// から始まるオリジンだけを書きます（パスやクエリ、* は使えません）。動作確認のための http://localhost:ポート番号 は書けます。";

/** まだ 1 件も許可していないときの 1 行。 */
export const ALLOWED_ORIGINS_EMPTY_NOTE =
  "まだ 1 件も許可していません。この状態では、ブラウザからの呼び出しはすべて断ります。";

/** キーが 1 本も無いときの 1 行。 */
export const API_KEYS_EMPTY_NOTE =
  "まだキーがありません。下の「キーを発行する」から作ってください。";

/* -------------------------------------------------------------------------
 * 新規動画の自動処理（W25 / FR27.4・FR27.5）
 * ----------------------------------------------------------------------- */

/**
 * 自動処理の説明。**既定は「切」である**ことを言葉で書く。
 *
 * 設定していないテナントで `lastDetectedAt` が空なのは「0 本だった」ではなく
 * 「まだ自動では処理していない」である。画面はそう出す。
 */
export const VIDEO_AUTO_INGEST_NOTE =
  "取り込まれた新しい動画を、1 日 1 回さがして自動で読み取ります。はじめは「切」で、ここで「入」にした会社だけが動きます。";

/** まだ 1 度も検知していないときの 1 行。**0 本と混ぜない**。 */
export const VIDEO_AUTO_INGEST_NEVER_DETECTED_NOTE =
  "まだ自動では処理していません（新しい動画が 0 本だった、という意味ではありません）。";

/** 月の本数の上限を置いていないときの 1 行。 */
export const VIDEO_AUTO_INGEST_NO_LIMIT_NOTE =
  "月の本数の上限は置いていません。費用そのものは、別に持っている月ごとの上限の内側で止まります。";

/** 上限で止まっているときの帯（FR27.6）。 */
export const VIDEO_AUTO_INGEST_SUSPENDED_NOTE =
  "今月の本数の上限に達したので、自動の読み取りは止まっています。上限を上げるか、来月になると再開します。";

/** 管理者以外に出す 1 行。判断の正本はサーバーの `editableByViewer`。 */
export const VIDEO_AUTO_INGEST_FORBIDDEN_NOTE =
  "この設定を変えられるのは、この会社の管理者だけです。";
